import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import TimeTabs from "@/admin/TimeTabs";
import { Search, ChevronLeft, ChevronRight, Download, Loader2 } from "lucide-react";

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">{title}</h1>
        {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-2">{children}</div>
    </div>
  );
}

export function Badge({ children, tone = "gray" }) {
  const tones = {
    gray: "bg-gray-100 text-gray-700", green: "bg-green-100 text-green-700",
    red: "bg-red-100 text-red-700", amber: "bg-amber-100 text-amber-700",
    blue: "bg-blue-100 text-blue-700", crimson: "bg-[#FDF2F2] text-[#8B0000]",
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${tones[tone] || tones.gray}`}>{children}</span>;
}

const SOURCE_TONE = { Imported: "blue", Live: "green", Demo: "amber" };

export default function ResourceTable({ title, subtitle, endpoint, columns, filters = [], searchable = true, exportEntity, extraParams = {}, refreshKey = 0, headerActions, withPeriod = false }) {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filterVals, setFilterVals] = useState({});
  const [tp, setTp] = useState({ period: "all", frm: "", to: "" });
  const [exporting, setExporting] = useState(false);
  const reqId = useRef(0);

  const buildParams = () => {
    const params = { ...extraParams };
    if (searchable && search) params.search = search;
    if (withPeriod) {
      if (tp.period === "custom") { if (tp.frm && tp.to) { params.period = "custom"; params.frm = tp.frm; params.to = tp.to; } }
      else if (tp.period !== "all") params.period = tp.period;
    }
    Object.entries(filterVals).forEach(([k, v]) => { if (v) params[k] = v; });
    return params;
  };

  const load = useCallback(async () => {
    setLoading(true);
    const my = ++reqId.current;
    try {
      const params = { page, page_size: 20, ...buildParams() };
      const { data } = await api.get(endpoint, { params });
      if (my !== reqId.current) return;
      setData(Array.isArray(data) ? { items: data, total: data.length, page: 1, pages: 1 } : data);
    } catch (e) { if (my === reqId.current) toast.error(apiErr(e)); } finally { if (my === reqId.current) setLoading(false); }
    // eslint-disable-next-line
  }, [endpoint, page, search, JSON.stringify(filterVals), refreshKey, JSON.stringify(extraParams), JSON.stringify(tp)]);

  useEffect(() => { load(); }, [load]);

  const doExport = async () => {
    setExporting(true);
    try {
      const rows = [];
      for (let p = 1; p <= 200; p += 1) {
        const { data } = await api.get(endpoint, { params: { ...buildParams(), page: p, page_size: 500 } });
        const items = Array.isArray(data) ? data : data.items;
        rows.push(...items);
        if (Array.isArray(data) || p >= (data.pages || 1)) break;
      }
      const cols = columns.filter((c) => !c.noExport);
      const cell = (c, r) => { const v = c.csv ? c.csv(r) : r[c.key]; return v === null || v === undefined || typeof v === "object" ? "" : String(v); };
      const esc = (s) => (/[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
      const csv = [cols.map((c) => esc(c.label)).join(","), ...rows.map((r) => cols.map((c) => esc(cell(c, r))).join(","))].join("\n");
      const url = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }));
      const suffix = withPeriod && tp.period !== "all" ? `_${tp.period === "custom" ? `${tp.frm}_to_${tp.to}` : tp.period}` : "";
      const a = document.createElement("a"); a.href = url; a.download = `${exportEntity}${suffix}.csv`; a.click();
      toast.success(`Exported ${rows.length} rows`);
    } catch (e) { toast.error(apiErr(e)); } finally { setExporting(false); }
  };

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle}>
        {headerActions}
        {withPeriod && <TimeTabs value={tp} onChange={(v) => { setPage(1); setTp(v); }} />}
        {exportEntity && (
          <button data-testid={`export-${exportEntity}-btn`} onClick={doExport} disabled={exporting}
            className="flex items-center gap-2 text-sm font-semibold border border-gray-300 px-3 py-2 rounded-lg hover:border-[#8B0000] hover:text-[#8B0000] disabled:opacity-60">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Export CSV
          </button>
        )}
      </PageHeader>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        {searchable && (
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input data-testid="resource-search" value={search} onChange={(e) => { setPage(1); setSearch(e.target.value); }}
              placeholder="Search..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-[#8B0000]" />
          </div>
        )}
        {filters.map((f) => (
          <select key={f.key} data-testid={`filter-${f.key}`} value={filterVals[f.key] || ""}
            onChange={(e) => { setPage(1); setFilterVals({ ...filterVals, [f.key]: e.target.value }); }}
            className="border border-gray-300 rounded-lg text-sm px-3 py-2 outline-none focus:border-[#8B0000] bg-white">
            <option value="">{f.label}</option>
            {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                {columns.map((c) => (
                  <th key={c.key} className="text-gray-600 font-bold uppercase text-[11px] tracking-wider py-3 px-4 whitespace-nowrap">{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={columns.length} className="py-12 text-center text-gray-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></td></tr>
              ) : data.items.length === 0 ? (
                <tr><td colSpan={columns.length} className="py-12 text-center text-gray-400">No records found</td></tr>
              ) : data.items.map((row, i) => (
                <tr key={row.id || i} className="border-b border-gray-100 hover:bg-gray-50/60" data-testid={`row-${row.id || i}`}>
                  {columns.map((c) => (
                    <td key={c.key} className="py-3 px-4 text-gray-700 whitespace-nowrap">
                      {c.render ? c.render(row) :
                        c.key === "data_source" ? <Badge tone={SOURCE_TONE[row[c.key]]}>{row[c.key]}</Badge> :
                        String(row[c.key] ?? "—")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-gray-500">
          <span>{data.total} records</span>
          <div className="flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="page-prev" className="p-1.5 rounded border border-gray-200 disabled:opacity-40"><ChevronLeft className="h-4 w-4" /></button>
            <span>Page {data.page} / {data.pages || 1}</span>
            <button disabled={page >= (data.pages || 1)} onClick={() => setPage(page + 1)} data-testid="page-next" className="p-1.5 rounded border border-gray-200 disabled:opacity-40"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
